import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../../api';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './MergeSaisonsPage.css';

// Serveurs autorisés à fusionner entre eux (même liste que le backend)
const CROSS_SERVER_IDS = ['1474102945934082298', '1479575292337979625', '1500581154854670531', '1547381818217599006'];
const SERVER_NAMES = {
    '1474102945934082298': 'VSA Mikhal',
    '1479575292337979625': 'VSA Dakal',
    '1500581154854670531': 'VSA Kourial',
    '1547381818217599006': 'Perco Mubarak - Kourial',
};

const fmtDate = (d) => new Date(d).toLocaleDateString(getLocale());
const fmtShort = (d) => new Date(d).toLocaleDateString(getLocale(), { month: 'short', year: '2-digit' });
const byNewest = (a, b) => new Date(b.startDate) - new Date(a.startDate);
const selectionKey = (ids) => [...ids].sort().join(',');

const hydrateMergedClassement = (rows = []) => rows
    .map((row) => {
        const baseTotal = Number(row?.totalPoints ?? row?.points ?? 0) || 0;
        const manualAdjustment = Number(row?.manualAdjustment ?? 0) || 0;
        return {
            ...row,
            baseTotal,
            manualAdjustment,
            totalPoints: baseTotal + manualAdjustment,
        };
    })
    .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));

const GROUP_LIMIT = 6;

// Groupe de saisons d'un serveur (cases à cocher) ; seules les plus récentes sont affichées par défaut
const SeasonGroup = ({ title, seasons, selectedIds, onToggle, loading, emptyText, searching }) => {
    const { t } = useLanguage();
    const [expanded, setExpanded] = useState(false);
    const showAll = expanded || searching;
    const shown = showAll ? seasons : seasons.filter((s, i) => i < GROUP_LIMIT || selectedIds.includes(s._id));
    const hiddenCount = seasons.length - shown.length;

    return (
        <div className="merge-group">
            <div className="merge-group__title">{title}</div>
            {loading ? (
                <p className="c2-field__hint">{t('common.loading', 'Chargement...')}</p>
            ) : seasons.length === 0 ? (
                <p className="c2-field__hint">{emptyText}</p>
            ) : (
                <>
                    <ul className="merge-season-list">
                        {shown.map((s) => {
                            const checked = selectedIds.includes(s._id);
                            return (
                                <li key={s._id}>
                                    <label className={`merge-season${checked ? ' is-checked' : ''}`} title={`${fmtDate(s.startDate)} → ${fmtDate(s.endDate)}`}>
                                        <input type="checkbox" className="c2-check" checked={checked} onChange={() => onToggle(s._id)} />
                                        <span className="merge-season__title">{s.title}</span>
                                        <span className="merge-season__dates">{fmtShort(s.endDate)}</span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                    {!searching && (hiddenCount > 0 || expanded) && (
                        <button type="button" className="merge-more" onClick={() => setExpanded(v => !v)}>
                            {expanded ? t('mergePage.showLess', 'Réduire') : t('mergePage.showMore', 'Voir les {n} autres saisons', { n: hiddenCount })}
                            <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={14} />
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

const MergeSaisonsPage = () => {
    const { serverId } = useParams();
    const location = useLocation();
    const { t } = useLanguage();
    const { error, success, warning } = useNotification();

    const isCrossEnabled = CROSS_SERVER_IDS.includes(serverId);
    const availablePartners = CROSS_SERVER_IDS.filter((id) => id !== serverId);
    const currentName = SERVER_NAMES[serverId] || 'Ce serveur';

    const [seasons, setSeasons] = useState([]);
    const [selectedSeasonIds, setSelectedSeasonIds] = useState([]);
    const [generatedClassement, setGeneratedClassement] = useState([]);
    const [classement, setClassement] = useState([]);
    const [generatedKey, setGeneratedKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState(false);
    const [usernames, setUsernames] = useState({});
    const [seasonSearch, setSeasonSearch] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [adjustDrafts, setAdjustDrafts] = useState({});   // { joueurId: texte en cours de saisie }

    // Serveurs partenaires
    const [partnerSeasonsMap, setPartnerSeasonsMap] = useState({});   // { [partnerId]: Season[] }
    const [partnerLoadingMap, setPartnerLoadingMap] = useState({});   // { [partnerId]: boolean }
    const [selectedPartnerSeasonIds, setSelectedPartnerSeasonIds] = useState([]);
    const [activePartnerIds, setActivePartnerIds] = useState([]);

    // Publication
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishTitle, setPublishTitle] = useState('');
    const [publishChannel, setPublishChannel] = useState('');
    const [channels, setChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [channelSearch, setChannelSearch] = useState('');
    const [publishing, setPublishing] = useState(false);

    // Fusions enregistrées (utilisables comme une saison, ex. fiches de réservation)
    const [fusions, setFusions] = useState([]);
    const [loadedFusion, setLoadedFusion] = useState(null);      // fusion chargée dans le résultat
    const [baseExcluded, setBaseExcluded] = useState([]);        // joueurs déjà retirés dans la fusion chargée
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveTitle, setSaveTitle] = useState('');
    const [savingFusion, setSavingFusion] = useState(false);

    const baseSeasonId = new URLSearchParams(location.search).get('base');

    // Saisons du serveur courant
    useEffect(() => {
        const fetchSeasons = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/servers/${serverId}/saisons`);
                setSeasons(Array.isArray(response.data) ? response.data : []);
            } catch (err) {
                console.error('❌ Erreur chargement saisons:', err);
                error(t('seasons.loadingError', 'Erreur chargement saison'));
            } finally {
                setLoading(false);
            }
        };
        if (serverId) fetchSeasons();
    }, [serverId, error, t]);

    useEffect(() => {
        if (!serverId) return;
        api.get(`/servers/${serverId}/saisons-fusion`)
            .then((res) => setFusions(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.warn('⚠️ Fusions enregistrées:', err.message));
    }, [serverId]);

    // Saison pré-sélectionnée via ?base=<id>
    useEffect(() => {
        if (!baseSeasonId || seasons.length === 0) return;
        if (!seasons.some((s) => s._id === baseSeasonId)) return;
        setSelectedSeasonIds((prev) => (prev.includes(baseSeasonId) ? prev : [baseSeasonId, ...prev]));
    }, [baseSeasonId, seasons]);

    const updatePlayerAdjustment = (joueurId, rawValue) => {
        // Champ texte : accepte le signe « - » et une saisie vide le temps de taper
        if (!/^-?d*$/.test(rawValue)) return;
        setAdjustDrafts(prev => ({ ...prev, [joueurId]: rawValue }));
        const parsed = rawValue === '' || rawValue === '-' ? 0 : Number(rawValue);
        const manualAdjustment = Number.isNaN(parsed) ? 0 : parsed;

        setClassement((prev) => hydrateMergedClassement(prev.map((row) => {
            if (row.joueurId !== joueurId) return row;
            return {
                ...row,
                manualAdjustment,
                totalPoints: (row.baseTotal || 0) + manualAdjustment,
            };
        })));
    };

    const removePlayerFromMerged = (joueurId) => {
        setClassement((prev) => prev.filter((row) => row.joueurId !== joueurId));
    };

    const resetLocalEdits = () => {
        if (generatedClassement.length === 0) {
            warning(t('mergePage.nothingToRestore', 'Aucun classement généré à restaurer.'));
            return;
        }
        setClassement(generatedClassement.map((row) => ({ ...row })));
        success(t('mergePage.localReset', '✅ Modifications locales annulées.'));
    };

    const openPublishModal = async () => {
        setPublishChannel('');
        setPublishTitle('');
        setChannelSearch('');
        setShowPublishModal(true);
        if (channels.length === 0) {
            setChannelsLoading(true);
            try {
                const res = await api.get(`/discord/channels/${serverId}`);
                const textChannels = (Array.isArray(res.data) ? res.data : [])
                    .filter((c) => c.type === 0)
                    .sort((a, b) => a.name.localeCompare(b.name));
                setChannels(textChannels);
            } catch (err) {
                console.error('❌ Erreur chargement salons:', err);
                warning(t('mergePage.channelsError', 'Impossible de charger la liste des salons Discord.'));
            } finally {
                setChannelsLoading(false);
            }
        }
    };

    const confirmPublish = async () => {
        if (!publishChannel) { warning(t('mergePage.pickChannel', 'Sélectionnez un salon de publication.')); return; }
        try {
            setPublishing(true);
            await api.post(`/servers/${serverId}/saisons/publish-merged`, {
                classement,
                channelId: publishChannel,
                rankingTitle: publishTitle.trim() || undefined,
            });
            success(t('mergePage.published', '✅ Classement fusionné publié avec succès !'));
            setShowPublishModal(false);
        } catch (err) {
            error(err.response?.data?.error || t('mergePage.publishError', 'Erreur lors de la publication.'));
        } finally {
            setPublishing(false);
        }
    };

    const exportToExcel = () => {
        const BOM = '﻿';
        const headers = [
            t('editSeason.csvRank', 'Rang'), t('editSeason.csvPlayer', 'Joueur'), t('editSeason.csvAttack', 'Pts Attaque'),
            t('editSeason.csvDefense', 'Pts Défense'), t('editSeason.csvTempo', 'Pts Tempo'), t('mergePage.csvLocal', 'Modif locale'),
            t('editSeason.csvTotal', 'Total'), t('mergePage.csvParticipations', 'Participations')
        ];
        const rows = classement.map((j, i) => [
            i + 1,
            usernames[j.joueurId] || j.joueurId,
            j.ptsAttaque || 0,
            j.ptsDefense || 0,
            j.tempoPoints || 0,
            j.manualAdjustment || 0,
            j.totalPoints ?? j.points ?? 0,
            j.participations || 0,
        ]);
        const csv = BOM + [headers, ...rows]
            .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `classement_fusion_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        success(t('mergePage.exported', '📥 Classement exporté !'));
    };

    // Active / désactive un serveur partenaire et charge ses saisons à la demande
    const togglePartner = async (pid) => {
        if (activePartnerIds.includes(pid)) {
            setActivePartnerIds((prev) => prev.filter((id) => id !== pid));
            const partnerSeasonIds = (partnerSeasonsMap[pid] || []).map((s) => s._id);
            setSelectedPartnerSeasonIds((prev) => prev.filter((id) => !partnerSeasonIds.includes(id)));
            return;
        }
        setActivePartnerIds((prev) => [...prev, pid]);
        if (partnerSeasonsMap[pid]) return;
        setPartnerLoadingMap((prev) => ({ ...prev, [pid]: true }));
        try {
            const res = await api.get(`/servers/${serverId}/saisons/cross-partner?partnerId=${pid}`);
            setPartnerSeasonsMap((prev) => ({ ...prev, [pid]: Array.isArray(res.data) ? res.data : [] }));
        } catch (err) {
            console.error('❌ Erreur chargement saisons partenaire:', err);
            error(t('mergePage.partnerError', 'Erreur chargement saisons du serveur partenaire.'));
            setActivePartnerIds((prev) => prev.filter((id) => id !== pid));
        } finally {
            setPartnerLoadingMap((prev) => ({ ...prev, [pid]: false }));
        }
    };

    const toggleSeason = (id) =>
        setSelectedSeasonIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const togglePartnerSeason = (id) =>
        setSelectedPartnerSeasonIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    // Noms des joueurs : serveur courant, puis serveurs partenaires, puis API Discord
    const loadUsernames = async (players, partnerIds) => {
        const ids = [...new Set(players.map((j) => j.joueurId).filter(Boolean))];
        if (ids.length === 0) return;

        const collected = {};
        let missing = ids;

        try {
            const res = await api.post('/players/display-names', { playerIds: missing, serverId });
            if (res.data?.success) {
                Object.assign(collected, res.data.displayNames || {});
                missing = res.data.notFound || [];
            }
        } catch (err) {
            console.warn('⚠️ Noms MongoDB (serveur courant):', err.message);
        }

        for (const pid of partnerIds) {
            if (missing.length === 0) break;
            try {
                const res = await api.post('/players/display-names', { playerIds: missing, serverId: pid });
                if (res.data?.success) {
                    Object.assign(collected, res.data.displayNames || {});
                    missing = res.data.notFound || [];
                }
            } catch (err) {
                console.warn(`⚠️ Noms MongoDB (${SERVER_NAMES[pid] || pid}):`, err.message);
            }
        }

        if (missing.length > 0) {
            try {
                const res = await api.post('/discord/users', { userIds: missing, serverId, forceRefresh: false });
                Object.assign(collected, res.data || {});
                missing = missing.filter((id) => !res.data?.[id]);
            } catch (err) {
                console.warn('⚠️ Noms Discord (serveur courant):', err.message);
            }
        }

        for (const pid of partnerIds) {
            if (missing.length === 0) break;
            try {
                const res = await api.post('/discord/users', { userIds: missing, serverId: pid, forceRefresh: false });
                Object.assign(collected, res.data || {});
                missing = missing.filter((id) => !res.data?.[id]);
            } catch (err) {
                console.warn(`⚠️ Noms Discord (${SERVER_NAMES[pid] || pid}):`, err.message);
            }
        }

        setUsernames((prev) => ({ ...prev, ...collected }));
    };

    const allSelectedIds = [...selectedSeasonIds, ...selectedPartnerSeasonIds];
    const currentKey = selectionKey(allSelectedIds);
    const isCrossMerge = selectedPartnerSeasonIds.length > 0;

    // Fusion locale, ou inter-serveurs dès qu'une saison partenaire est cochée
    const mergeRankings = async () => {
        if (allSelectedIds.length < 2) {
            error(isCrossMerge ? t('mergePage.min2Total', 'Sélectionnez au moins 2 saisons au total.') : t('mergePage.min2', 'Sélectionnez au moins 2 saisons.'));
            return;
        }
        try {
            setMerging(true);
            const res = isCrossMerge
                ? await api.post(`/servers/${serverId}/saisons/merge-classement-cross`, { saisonIds: allSelectedIds })
                : await api.post(`/servers/${serverId}/saisons/merge-classement`, { saisonIds: selectedSeasonIds });
            const merged = hydrateMergedClassement(Array.isArray(res.data?.classement) ? res.data.classement : []);
            setGeneratedClassement(merged.map((row) => ({ ...row })));
            setClassement(merged);
            setGeneratedKey(currentKey);
            setBaseExcluded([]);
            await loadUsernames(merged, isCrossMerge ? activePartnerIds : []);
        } catch (err) {
            error(err.response?.data?.error || (isCrossMerge ? t('mergePage.crossError', 'Erreur lors de la fusion inter-serveurs.') : t('mergePage.mergeError', 'Erreur lors de la fusion des classements.')));
        } finally {
            setMerging(false);
        }
    };

    // Charge une fusion enregistrée : sélection, classement (ajustements et retraits compris)
    const loadFusion = async (fusion) => {
        try {
            setMerging(true);
            const ids = fusion.saisonIds || [];
            const localIds = ids.filter((id) => seasons.some((s) => s._id === id));
            const otherIds = ids.filter((id) => !localIds.includes(id));

            // Saisons d'autres serveurs : active les serveurs partenaires concernés
            const partnersUsed = [];
            if (otherIds.length > 0 && isCrossEnabled) {
                const map = { ...partnerSeasonsMap };
                for (const pid of availablePartners) {
                    if (!map[pid]) {
                        const res = await api.get(`/servers/${serverId}/saisons/cross-partner?partnerId=${pid}`).catch(() => ({ data: [] }));
                        map[pid] = Array.isArray(res.data) ? res.data : [];
                    }
                    if (map[pid].some((s) => otherIds.includes(s._id))) partnersUsed.push(pid);
                }
                setPartnerSeasonsMap(map);
                setActivePartnerIds((prev) => [...new Set([...prev, ...partnersUsed])]);
            }
            setSelectedSeasonIds(localIds);
            setSelectedPartnerSeasonIds(otherIds);

            const res = await api.get(`/saisons-fusion/${fusion._id}/classement`);
            // L'API renvoie le total ajustement compris : on repart de la base
            const rows = (Array.isArray(res.data) ? res.data : [])
                .map((row) => ({ ...row, totalPoints: (row.totalPoints || 0) - (row.manualAdjustment || 0) }));
            const merged = hydrateMergedClassement(rows);
            setGeneratedClassement(merged.map((row) => ({ ...row })));
            setClassement(merged);
            setGeneratedKey(selectionKey(ids));
            setAdjustDrafts({});
            setLoadedFusion(fusion);
            setBaseExcluded(fusion.excludedJoueurIds || []);
            await loadUsernames(merged, partnersUsed);
        } catch (err) {
            error(err.response?.data?.error || t('mergePage.loadFusionError', 'Impossible de charger cette fusion.'));
        } finally {
            setMerging(false);
        }
    };

    const deleteFusion = async (fusion) => {
        if (!window.confirm(t('mergePage.deleteFusionConfirm', 'Supprimer la fusion « {title} » ? Les fiches de réservation qui l\'utilisent n\'auront plus de classement.', { title: fusion.title }))) return;
        try {
            await api.delete(`/saisons-fusion/${fusion._id}`);
            setFusions((prev) => prev.filter((f) => f._id !== fusion._id));
            if (loadedFusion?._id === fusion._id) setLoadedFusion(null);
            success(t('mergePage.fusionDeleted', '🗑️ Fusion supprimée.'));
        } catch (err) {
            error(err.response?.data?.error || t('mergePage.deleteFusionError', 'Erreur lors de la suppression.'));
        }
    };

    const openSaveModal = () => {
        const titles = (generatedKey || '').split(',').map((id) => seasonTitle(id).title);
        setSaveTitle(loadedFusion?.title || `${t('mergePage.fusionPrefix', 'Fusion')} - ${titles.join(` ${t('mergePage.and', 'et')} `)}`);
        setShowSaveModal(true);
    };

    // asNew : enregistre une nouvelle fusion même si une fusion est chargée
    const saveFusion = async (asNew) => {
        const title = saveTitle.trim();
        if (!title) { warning(t('mergePage.fusionTitleRequired', 'Donnez un nom à la fusion.')); return; }
        const kept = new Set(classement.map((row) => row.joueurId));
        const payload = {
            title,
            saisonIds: generatedKey.split(','),
            adjustments: classement
                .filter((row) => row.manualAdjustment)
                .map((row) => ({ joueurId: row.joueurId, manualAdjustment: row.manualAdjustment })),
            excludedJoueurIds: [...new Set([
                ...baseExcluded,
                ...generatedClassement.map((row) => row.joueurId).filter((id) => !kept.has(id)),
            ])],
        };
        try {
            setSavingFusion(true);
            const res = loadedFusion && !asNew
                ? await api.put(`/saisons-fusion/${loadedFusion._id}`, payload)
                : await api.post(`/servers/${serverId}/saisons-fusion`, payload);
            const saved = res.data;
            setFusions((prev) => [saved, ...prev.filter((f) => f._id !== saved._id)]);
            setLoadedFusion(saved);
            setBaseExcluded(saved.excludedJoueurIds || []);
            setShowSaveModal(false);
            success(t('mergePage.fusionSaved', '✅ Fusion enregistrée : elle est disponible dans les fiches de réservation.'));
        } catch (err) {
            error(err.response?.data?.error || t('mergePage.saveFusionError', 'Erreur lors de l\'enregistrement de la fusion.'));
        } finally {
            setSavingFusion(false);
        }
    };

    const clearSelection = () => {
        setSelectedSeasonIds([]);
        setSelectedPartnerSeasonIds([]);
    };

    // Saisons affichées : recherche, archivées masquées par défaut, plus récentes d'abord
    const filterSeasons = (list) => list
        .filter((s) => showArchived || !s.archived || selectedSeasonIds.includes(s._id) || selectedPartnerSeasonIds.includes(s._id))
        .filter((s) => s.title.toLowerCase().includes(seasonSearch.toLowerCase()))
        .sort(byNewest);

    const visibleSeasons = useMemo(
        () => filterSeasons(seasons),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [seasons, showArchived, seasonSearch, selectedSeasonIds, selectedPartnerSeasonIds]
    );

    const archivedCount = seasons.filter((s) => s.archived).length
        + Object.values(partnerSeasonsMap).flat().filter((s) => s.archived).length;

    const seasonTitle = (id) => {
        const local = seasons.find((s) => s._id === id);
        if (local) return { title: local.title, server: isCrossEnabled ? currentName : null };
        for (const [pid, list] of Object.entries(partnerSeasonsMap)) {
            const found = list.find((s) => s._id === id);
            if (found) return { title: found.title, server: SERVER_NAMES[pid] || pid };
        }
        return { title: id, server: null };
    };

    const hasResult = classement.length > 0;
    const isStale = hasResult && generatedKey !== currentKey;
    const filteredChannels = channels.filter((c) => c.name.toLowerCase().includes(channelSearch.toLowerCase()));

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="copy"
                title={t('mergePage.title', 'Fusion des classements')}
                subtitle={t('mergePage.subtitle', 'Cumulez plusieurs saisons, d\'un ou plusieurs serveurs, dans un seul classement')}
                actions={
                    <Link to={`/server/${serverId}/saison`} className="c2-btn c2-btn--ghost">
                        <Icon name="arrowLeft" size={18} />
                        {t('navigation.seasons', 'Saisons')}
                    </Link>
                }
            />

            <div className="merge-layout">
                {/* ── Sélection des saisons ── */}
                <aside className="c2-card merge-picker">
                    <div className="merge-picker__head">
                        <h2 className="c2-section__title">{t('mergePage.toMerge', 'Saisons à fusionner')}</h2>

                        {/* Bouton de génération en haut : toujours visible, même avec de longues listes */}
                        <div className="merge-picker__action">
                            <span className="c2-field__hint">
                                {allSelectedIds.length > 1
                                    ? t('mergePage.selectedN', '{n} saisons sélectionnées', { n: allSelectedIds.length })
                                    : t('mergePage.selected1', '{n} saison sélectionnée', { n: allSelectedIds.length })}
                                {allSelectedIds.length > 0 && (
                                    <button type="button" className="merge-link" onClick={clearSelection}>{t('mergePage.uncheckAll', 'Tout décocher')}</button>
                                )}
                            </span>
                            <button
                                type="button"
                                className="c2-btn c2-btn--primary c2-btn--block"
                                onClick={mergeRankings}
                                disabled={allSelectedIds.length < 2 || merging}
                            >
                                <Icon name="trophy" size={18} />
                                {merging ? t('mergePage.merging', 'Fusion en cours...') : hasResult ? t('mergePage.regenerate', 'Régénérer le classement') : t('mergePage.generate', 'Générer le classement')}
                            </button>
                        </div>


                        {/* Serveurs : en haut pour ne pas avoir à défiler */}
                        {isCrossEnabled && (
                            <div className="merge-servers">
                                <span className="merge-group__title">{t('mergePage.servers', 'Serveurs')}</span>
                                <div className="merge-partners__chips">
                                    <span className="merge-partner is-current">
                                        <Icon name="check" size={14} />
                                        {currentName}
                                    </span>
                                    {availablePartners.map((pid) => {
                                        const active = activePartnerIds.includes(pid);
                                        return (
                                            <button
                                                key={pid}
                                                type="button"
                                                className={`merge-partner${active ? ' is-active' : ''}`}
                                                onClick={() => togglePartner(pid)}
                                                aria-pressed={active}
                                                title={active ? t('mergePage.removeServer', 'Retirer ce serveur') : t('mergePage.addServer', 'Ajouter les saisons de ce serveur')}
                                            >
                                                <Icon name={active ? 'check' : 'plus'} size={14} />
                                                {SERVER_NAMES[pid]}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <input
                            type="search"
                            className="c2-input c2-input--sm"
                            placeholder={t('mergePage.searchSeason', 'Rechercher une saison...')}
                            aria-label={t('mergePage.searchSeasonLabel', 'Rechercher une saison')}
                            value={seasonSearch}
                            onChange={(e) => setSeasonSearch(e.target.value)}
                        />
                        {archivedCount > 0 && (
                            <label className="merge-picker__archived">
                                <input type="checkbox" className="c2-check" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                                {t('mergePage.showArchived', 'Afficher les saisons archivées ({n})', { n: archivedCount })}
                            </label>
                        )}
                    </div>

                    <div className="merge-picker__body">
                        {fusions.length > 0 && (
                            <div className="merge-group">
                                <div className="merge-group__title">{t('mergePage.savedFusions', 'Fusions enregistrées')}</div>
                                <ul className="merge-season-list">
                                    {fusions.map((f) => (
                                        <li key={f._id} className="merge-fusion">
                                            <button
                                                type="button"
                                                className={`merge-season merge-fusion__load${loadedFusion?._id === f._id ? ' is-checked' : ''}`}
                                                onClick={() => loadFusion(f)}
                                                disabled={merging}
                                                title={t('mergePage.loadFusion', 'Charger cette fusion')}
                                            >
                                                <Icon name="copy" size={14} />
                                                <span className="merge-season__title">{f.title}</span>
                                                <span className="merge-season__dates">{t('mergePage.nSeasons', '{n} saisons', { n: (f.saisonIds || []).length })}</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                                onClick={() => deleteFusion(f)}
                                                aria-label={t('mergePage.deleteFusion', 'Supprimer la fusion {title}', { title: f.title })}
                                                title={t('mergePage.deleteFusionShort', 'Supprimer')}
                                            >
                                                <Icon name="trash" size={14} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <SeasonGroup
                            title={isCrossEnabled ? currentName : t('mergePage.thisServer', 'Ce serveur')}
                            seasons={visibleSeasons}
                            selectedIds={selectedSeasonIds}
                            onToggle={toggleSeason}
                            loading={loading}
                            searching={!!seasonSearch}
                            emptyText={t('mergePage.noSeason', 'Aucune saison disponible.')}
                        />

                        {activePartnerIds.map((pid) => (
                            <SeasonGroup
                                key={pid}
                                title={SERVER_NAMES[pid]}
                                seasons={filterSeasons(partnerSeasonsMap[pid] || [])}
                                selectedIds={selectedPartnerSeasonIds}
                                onToggle={togglePartnerSeason}
                                loading={partnerLoadingMap[pid]}
                                searching={!!seasonSearch}
                                emptyText={t('mergePage.noSeasonServer', 'Aucune saison disponible sur ce serveur.')}
                            />
                        ))}
                    </div>

                </aside>

                {/* ── Résultat ── */}
                <section className="c2-card merge-result">
                    {!hasResult ? (
                        <div className="c2-empty merge-result__empty">
                            {merging ? (
                                <>
                                    <div className="c2-spinner" />
                                    <p>{t('mergePage.merging', 'Fusion en cours...')}</p>
                                </>
                            ) : (
                                <>
                                    <Icon name="trophy" size={40} />
                                    <h2>{t('mergePage.noResult', 'Aucun classement généré')}</h2>
                                    <p>{t('mergePage.noResultHint', 'Cochez au moins 2 saisons à gauche, puis cliquez sur « Générer le classement ».')}</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="merge-result__head">
                                <div>
                                    <h2 className="c2-section__title">{loadedFusion ? loadedFusion.title : t('mergePage.merged', 'Classement fusionné')}</h2>
                                    <p className="c2-field__hint">{t('mergePage.resultMeta', '{players} joueurs · {seasons} saisons', { players: classement.length, seasons: generatedKey.split(',').length })}</p>
                                </div>
                                <div className="merge-result__actions">
                                    <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={resetLocalEdits}>
                                        <Icon name="refresh" size={16} />
                                        {t('mergePage.undo', 'Annuler les modifications')}
                                    </button>
                                    <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={exportToExcel}>
                                        <Icon name="archive" size={16} />
                                        {t('mergePage.export', 'Exporter (.csv)')}
                                    </button>
                                    <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={openSaveModal} disabled={isStale}>
                                        <Icon name="check" size={16} />
                                        {t('mergePage.saveFusion', 'Enregistrer la fusion')}
                                    </button>
                                    <button type="button" className="c2-btn c2-btn--accent c2-btn--sm" onClick={openPublishModal}>
                                        <Icon name="arrowRight" size={16} />
                                        {t('editSeason.publish', 'Publier')}
                                    </button>
                                </div>
                            </div>

                            <div className="merge-result__seasons">
                                {generatedKey.split(',').map((id) => {
                                    const info = seasonTitle(id);
                                    return (
                                        <span key={id} className="c2-chip">
                                            {info.title}
                                            {info.server && <span className="c2-muted">· {info.server}</span>}
                                        </span>
                                    );
                                })}
                            </div>

                            {isStale && (
                                <div className="merge-stale" role="status">
                                    <span>{t('mergePage.stale', 'La sélection a changé depuis la génération.')}</span>
                                    <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={mergeRankings} disabled={allSelectedIds.length < 2 || merging}>
                                        {t('mergePage.regenerateShort', 'Régénérer')}
                                    </button>
                                </div>
                            )}

                            <div className="merge-table-wrap">
                                <table className="c2-table merge-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t('editSeason.player', 'Joueur')}</th>
                                            <th className="is-num">{t('editSeason.attack', 'Attaque')}</th>
                                            <th className="is-num">{t('editSeason.defense', 'Défense')}</th>
                                            <th className="is-num">{t('editSeason.tempo', 'Tempo')}</th>
                                            <th className="is-num">{t('editSeason.adjustment', 'Ajustement')}</th>
                                            <th className="is-num">{t('editSeason.total', 'Total')}</th>
                                            <th className="is-num" title={t('mergePage.csvParticipations', 'Participations')}>{t('mergePage.partShort', 'Partic.')}</th>
                                            <th><span className="visually-hidden">{t('common.actions', 'Actions')}</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {classement.map((joueur, index) => {
                                            const nom = usernames[joueur.joueurId];
                                            return (
                                                <tr key={joueur.joueurId}>
                                                    <td className={`merge-rank merge-rank--${index + 1}`}>{index + 1}</td>
                                                    <td>{nom ? <strong>{nom}</strong> : <span className="c2-muted">{joueur.joueurId}</span>}</td>
                                                    <td className="is-num">{joueur.ptsAttaque > 0 ? joueur.ptsAttaque : <span className="c2-muted">–</span>}</td>
                                                    <td className="is-num">{joueur.ptsDefense > 0 ? joueur.ptsDefense : <span className="c2-muted">–</span>}</td>
                                                    <td className="is-num">{joueur.tempoPoints > 0 ? joueur.tempoPoints : <span className="c2-muted">–</span>}</td>
                                                    <td className="is-num">
                                                        <input
                                                            type="text"
                                                            className="c2-input c2-input--sm merge-adjust"
                                                            value={adjustDrafts[joueur.joueurId] ?? (joueur.manualAdjustment ? String(joueur.manualAdjustment) : '')}
                                                            placeholder="0"
                                                            onChange={(e) => updatePlayerAdjustment(joueur.joueurId, e.target.value)}
                                                            onFocus={(e) => e.target.select()}
                                                            onBlur={() => setAdjustDrafts(prev => { const next = { ...prev }; delete next[joueur.joueurId]; return next; })}
                                                            title={t('mergePage.adjustTitle', 'Ajouter/retirer des points localement')}
                                                            aria-label={t('mergePage.adjustFor', 'Ajustement : {name}', { name: nom || joueur.joueurId })}
                                                        />
                                                    </td>
                                                    <td className="is-num"><strong className="merge-total">{joueur.totalPoints ?? joueur.points ?? 0}</strong></td>
                                                    <td className="is-num c2-muted">{joueur.participations || 0}</td>
                                                    <td className="is-num">
                                                        <button
                                                            type="button"
                                                            className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                                            onClick={() => removePlayerFromMerged(joueur.joueurId)}
                                                            title={t('mergePage.removePlayer', 'Retirer ce joueur du classement fusionné')}
                                                            aria-label={t('mergePage.removeName', 'Retirer {name}', { name: nom || joueur.joueurId })}
                                                        >
                                                            <Icon name="trash" size={16} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </section>
            </div>

            {/* ── Enregistrement de la fusion ── */}
            {showSaveModal && (
                <div className="c2-modal-backdrop" role="presentation">
                    <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="merge-save-title">
                        <div className="c2-modal__head">
                            <h2 id="merge-save-title">{t('mergePage.saveFusion', 'Enregistrer la fusion')}</h2>
                            <button type="button" className="c2-icon-btn" onClick={() => setShowSaveModal(false)} disabled={savingFusion} aria-label={t('common.close', 'Fermer')}>
                                <Icon name="close" size={20} />
                            </button>
                        </div>

                        <label className="c2-field">
                            <span className="c2-field__label">{t('mergePage.fusionName', 'Nom de la fusion')}</span>
                            <input
                                type="text"
                                className="c2-input"
                                value={saveTitle}
                                onChange={(e) => setSaveTitle(e.target.value)}
                                placeholder={t('mergePage.fusionNamePlaceholder', 'Ex : Fusion - Saison 15 et 16')}
                                autoFocus
                            />
                        </label>
                        <p className="c2-field__hint">{t('mergePage.saveFusionHint', 'La fusion apparaîtra comme une saison dans les fiches de réservation. Son classement suit les saisons sources ; vos ajustements et joueurs retirés sont conservés.')}</p>

                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={() => setShowSaveModal(false)} disabled={savingFusion}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            {loadedFusion && (
                                <button type="button" className="c2-btn c2-btn--ghost" onClick={() => saveFusion(true)} disabled={savingFusion}>
                                    {t('mergePage.saveAsNew', 'Enregistrer comme nouvelle')}
                                </button>
                            )}
                            <button type="button" className="c2-btn c2-btn--primary" onClick={() => saveFusion(false)} disabled={savingFusion || !saveTitle.trim()}>
                                {savingFusion
                                    ? t('editSeason.saving', 'Enregistrement...')
                                    : loadedFusion ? t('mergePage.updateFusion', 'Mettre à jour') : t('editSeason.save', 'Enregistrer')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Publication ── */}
            {showPublishModal && (
                <div className="c2-modal-backdrop" role="presentation">
                    <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="merge-publish-title">
                        <div className="c2-modal__head">
                            <h2 id="merge-publish-title">{t('mergePage.publishTitle', 'Publier le classement fusionné')}</h2>
                            <button type="button" className="c2-icon-btn" onClick={() => setShowPublishModal(false)} disabled={publishing} aria-label={t('common.close', 'Fermer')}>
                                <Icon name="close" size={20} />
                            </button>
                        </div>

                        <label className="c2-field">
                            <span className="c2-field__label">{t('mergePage.customTitle', 'Titre personnalisé (optionnel)')}</span>
                            <input
                                type="text"
                                className="c2-input"
                                value={publishTitle}
                                onChange={(e) => setPublishTitle(e.target.value)}
                                placeholder={t('mergePage.customTitlePlaceholder', 'Ex : Classement VSA Dakal × Mikhal — Mai 2026')}
                            />
                        </label>

                        <div className="c2-field">
                            <span className="c2-field__label">
                                {t('createSeasonModal.publicationChannel', 'Salon de publication')}
                                {!channelsLoading && channels.length > 0 && <span className="c2-muted"> · {t('mergePage.channelsN', '{n} salons', { n: channels.length })}</span>}
                            </span>
                            {channelsLoading ? (
                                <p className="c2-field__hint">{t('mergePage.loadingChannels', 'Chargement des salons...')}</p>
                            ) : channels.length === 0 ? (
                                <p className="merge-warning">{t('mergePage.noChannel', 'Aucun salon disponible. Vérifiez que le bot est présent sur ce serveur.')}</p>
                            ) : (
                                <>
                                    <input
                                        type="search"
                                        className="c2-input c2-input--sm"
                                        value={channelSearch}
                                        onChange={(e) => setChannelSearch(e.target.value)}
                                        placeholder={t('editSeason.searchChannel', 'Rechercher un salon...')}
                                        aria-label={t('editSeason.searchChannelLabel', 'Rechercher un salon')}
                                    />
                                    <ul className="merge-channels">
                                        {filteredChannels.map((c) => (
                                            <li key={c.id}>
                                                <button
                                                    type="button"
                                                    className={`merge-channel${publishChannel === c.id ? ' is-active' : ''}`}
                                                    onClick={() => setPublishChannel(c.id)}
                                                    aria-pressed={publishChannel === c.id}
                                                >
                                                    <Icon name="hash" size={14} />
                                                    {c.name}
                                                    {publishChannel === c.id && <Icon name="check" size={16} className="merge-channel__check" />}
                                                </button>
                                            </li>
                                        ))}
                                        {filteredChannels.length === 0 && <li className="c2-field__hint merge-channels__none">{t('mergePage.noChannelMatch', 'Aucun salon correspondant.')}</li>}
                                    </ul>
                                </>
                            )}
                        </div>

                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={() => setShowPublishModal(false)} disabled={publishing}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="c2-btn c2-btn--accent" onClick={confirmPublish} disabled={!publishChannel || publishing}>
                                {publishing ? t('editSeason.publishing', 'Publication...') : t('editSeason.publish', 'Publier')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MergeSaisonsPage;
